/**
 * SpecialTablesListPage Component
 * 
 * Página de listagem de Tabelas Especiais de Preços.
 * Exibe todas as tabelas em formato de cards.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, Plus, ChevronRight, Percent, Package, Building2,
  Edit3, Trash2, Loader2
} from 'lucide-react';
import { specialPricingTablesService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { usePermissions, MODULES, ACTIONS } from '../../../../hooks/usePermissions';
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal';
import { Toast } from '../../../../components';
import './SpecialTablesListPage.css';

const SpecialTablesListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { toast, showToast, hideToast } = useToast();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const canManageTables = hasPermission(MODULES.SALES, ACTIONS.SALES_SPECIAL_TABLES_MANAGE);

  const loadTables = useCallback(async () => {
    setLoading(true);
    try {
      const response = await specialPricingTablesService.getAll({ limit: 100 });
      if (response.success) {
        setTables(response.data || []);
      } else {
        showToast('Erro ao carregar tabelas', 'error');
      }
    } catch (error) {
      console.error('Erro ao carregar tabelas:', error);
      showToast('Erro ao carregar tabelas', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  const handleDelete = (table, event) => {
    event?.stopPropagation();
    setConfirmModal({
      isOpen: true,
      title: 'Excluir tabela',
      message: `Deseja realmente excluir a tabela "${table.name}"? Esta ação não pode ser desfeita.`,
      onConfirm: async () => {
        setDeletingId(table.id);
        try {
          const response = await specialPricingTablesService.delete(table.id);
          if (response.success) {
            showToast('Tabela excluída com sucesso', 'success');
            await loadTables();
          } else {
            showToast(response.message || 'Erro ao excluir tabela', 'error');
          }
        } catch (error) {
          console.error('Erro ao excluir tabela:', error);
          showToast(error.response?.data?.message || 'Erro ao excluir tabela', 'error');
        } finally {
          setDeletingId(null);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="special-tables-list-page loading">
        <Loader2 className="spinner" size={32} />
        <p>Carregando tabelas...</p>
      </div>
    );
  }

  return (
    <div className="special-tables-list-page">
      <div className="page-header">
        <div>
          <h1>Tabelas <span className="highlight">Especiais</span></h1>
          <p className="subtitle">Gestão de Preçificação por Perfil de Cliente</p>
        </div>
        {canManageTables && (
          <button 
            className="btn-primary btn-new-table"
            onClick={() => navigate('/erp/vendas/tabelas-especiais/nova')}
          >
            <Plus size={16} />
            Nova Tabela
          </button>
        )}
      </div>

      <div className="tables-grid">
        {tables.length === 0 ? (
          <div className="empty-state">
            <Table size={48} />
            <p>Nenhuma tabela especial cadastrada</p>
            {canManageTables && (
              <button 
                className="btn-link"
                onClick={() => navigate('/erp/vendas/tabelas-especiais/nova')}
              >
                Criar primeira tabela
              </button>
            )}
          </div>
        ) : (
          tables.map((table) => (
            <div key={table.id} className="table-card">
              <div className="card-header">
                <div className="card-icon">
                  <Table size={28} />
                </div>
                <div className="card-actions">
                  <button 
                    className="btn-icon"
                    onClick={() => navigate(`/erp/vendas/tabelas-especiais/${table.id}`)}
                  >
                    <Edit3 size={16} />
                  </button>
                  {canManageTables && (
                    <button
                      type="button"
                      className="btn-icon danger"
                      title="Excluir tabela"
                      disabled={deletingId === table.id}
                      onClick={(e) => handleDelete(table, e)}
                    >
                      {deletingId === table.id ? (
                        <Loader2 size={16} className="spinner" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  )}
                </div>
              </div>
              
              <div className="card-content">
                <h3>{table.name}</h3>
                <div className="card-meta">
                  <Building2 size={12} />
                  <span>{table.client_name || table.is_general ? 'Tabela Geral' : 'Sem cliente'}</span>
                </div>
              </div>

              <div className="card-footer">
                <div className="card-stat">
                  <Percent size={10} />
                  <span className="stat-label">Margem</span>
                  <span className="stat-value">{table.global_margin || 0}%</span>
                </div>
                <div className="card-stat">
                  <Package size={10} />
                  <span className="stat-label">Itens</span>
                  <span className="stat-value">{table.items_count || 0}</span>
                </div>
              </div>

              <button 
                className="btn-access"
                onClick={() => navigate(`/erp/vendas/tabelas-especiais/${table.id}`)}
              >
                Acessar Configuração <ChevronRight size={14} />
              </button>
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          show={toast.show}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default SpecialTablesListPage;
